var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fontSizeRange, generateShortUUID } from '@project/utils';
import { FontFamilies } from '@project/components/Forms/Fields/FontPicker';
import Popover from '../../../Popover';
import ColorPicker from '../ColorPicker';
import { cleanRichText } from './helpers';
import { ReactQuillComponent } from '../styles';
import ImageResize from 'quill-image-resize-module-react';
import ImageFormat from './imageFormat';
import Modal from '../../../Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MediaModal } from '@appspotr/ui-kit';
import { faBrush } from '@fortawesome/pro-solid-svg-icons';
import { COLORS } from '../../../../themes';
var fontSizes = fontSizeRange(8, 80).map(function (fontSize) { return "".concat(fontSize, "px"); });
var Size = Quill.import('attributors/style/size');
Size.whitelist = fontSizes;
var Header = Quill.import('formats/header');
var Link = Quill.import('formats/link');
// override sanitization function
Link.sanitize = function sanitizeLink(value) {
    return value;
};
var Parchment = Quill.import('parchment');
var FontStyle = new Parchment.Attributor.Style('font', 'font-family', {
    scope: Parchment.Scope.INLINE,
    whitelist: FontFamilies,
});
var AlignStyle = new Parchment.Attributor.Style('align', 'text-align', {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['center', 'right'],
});
Quill.register(Size, true);
Quill.register(FontStyle, true);
Quill.register(AlignStyle, true);
Quill.register(ImageFormat, true);
Quill.register(Header, true);
Quill.register('modules/imageResize', ImageResize);
var formatFontStr = function (font) { return (font.indexOf(' ') > -1 ? "\"".concat(font, "\"") : font); };
var RTEditor = /** @class */ (function (_super) {
    __extends(RTEditor, _super);
    function RTEditor(props) {
        var _this = _super.call(this, props) || this;
        _this.handleChange = function (value) {
            var imgRegex = /<img.*?src="(.*?)"+>/g;
            var onChange = _this.props.onChange;
            if (!imgRegex.test(value)) {
                if (value !== _this.props.value) {
                    var cleanValue = cleanRichText(value);
                    if (cleanValue.trim() === '' && !_this.props.value) {
                        return onChange(null);
                    }
                    return onChange(cleanValue.trim() !== '' ? value : '');
                }
            }
            var newValue = value.replace('float: right', 'margin-left: auto !important;');
            newValue = newValue.replace('float: left', 'margin-right: auto !important;');
            newValue = newValue.replace('display: inline;', 'display: block;');
            return onChange(newValue);
        };
        _this.onSelectFile = function (image) {
            if (_this.quillRef) {
                var range = _this.quillRef.getEditor().getSelection();
                _this.quillRef
                    .getEditor()
                    .insertEmbed((range === null || range === void 0 ? void 0 : range.index) ? range.index : 0, 'image', "".concat(image.url));
                _this.quillRef.getEditor().update();
                _this.setState({ displayPopover: false });
                // when adding images the handle-change is not triggered with set width and height, so we have to manually trigger handle-change using the dom with a small amount of delay
                var timer_1 = setTimeout(function () {
                    var editor = document.getElementById(_this.quillID);
                    var value = editor.innerHTML || null;
                    _this.handleChange(value);
                    if (timer_1)
                        clearTimeout(timer_1);
                }, 100);
            }
        };
        _this.renderToolbar = function () {
            var _a;
            return (_jsxs("div", __assign({ id: "ql-editor-toolbar".concat(_this.quillID), style: {
                    border: 'none',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    borderLeft: "solid 1px ".concat(COLORS.mutedGray),
                    borderTop: "solid 1px ".concat(COLORS.mutedGray),
                    borderRight: "solid 1px ".concat(COLORS.mutedGray),
                } }, { children: [_jsxs("span", __assign({ className: "ql-formats", style: {
                            marginRight: 0,
                        } }, { children: [_jsxs("select", __assign({ className: "ql-header", defaultValue: '', onChange: function (e) { return e.persist(); } }, { children: [_jsx("option", { value: "1" }), _jsx("option", { value: "2" }), _jsx("option", { value: "3" }), _jsx("option", { value: "4" }), _jsx("option", { value: "5" }), _jsx("option", { value: "6" }), _jsx("option", { selected: true })] })), _this.renderFontPicker(), _this.renderSizePicker()] })), _jsxs("span", __assign({ className: "ql-formats" }, { children: [_jsx("button", { className: "ql-bold", type: "button" }), _jsx("button", { className: "ql-italic", type: "button" }), _jsx("button", { className: "ql-underline", type: "button" }), _jsx("button", { className: "ql-strike", type: "button" })] })), _jsxs("span", __assign({ className: "ql-formats" }, { children: [_this.renderColorPicker('color'), _this.renderColorPicker('background')] })), _jsxs("span", __assign({ className: "ql-formats" }, { children: [_jsx("button", { className: "ql-link", type: "button" }), _this.renderImageRender()] })), _jsxs("span", __assign({ className: "ql-formats" }, { children: [_jsx("button", { className: "ql-align", value: "", type: "button" }), _jsx("button", { className: "ql-align", value: "center", type: "button" }), _jsx("button", { className: "ql-align", value: "right", type: "button" })] })), _jsxs("span", __assign({ className: "ql-formats" }, { children: [_jsx("button", { className: "ql-list", value: "ordered", type: "button" }), _jsx("button", { className: "ql-list", value: "bullet", type: "button" })] })), ((_a = _this.props) === null || _a === void 0 ? void 0 : _a.sideBarRichText) && (_jsx("span", __assign({ className: "ql-formats" }, { children: _jsx(FontAwesomeIcon, { icon: faBrush, onClick: function () {
                                _this.props.richTextCustomModal(true);
                            } }) })))] })));
        };
        _this.toggleMediaExplorer = function () {
            _this.setState(function (prevState) { return ({
                displayPopover: !prevState.displayPopover,
            }); });
        };
        _this.closePopover = function (e) {
            if (!_this.mediaFolderPopoverRef ||
                !_this.mediaFolderPopoverRef.current ||
                (_this.mediaFolderPopoverRef &&
                    _this.mediaFolderPopoverRef.current &&
                    !_this.mediaFolderPopoverRef.current.contains(e.target))) {
                _this.setState({
                    displayPopover: false,
                });
            }
        };
        _this.renderImageRender = function () {
            return (_jsx("button", { className: "ql-image", type: "button", onClick: function () { return _this.toggleMediaExplorer(); } }));
        };
        _this.togglePopover = function (type) {
            _this.setState(function (prevState) {
                var _a;
                return (_a = {},
                    _a[type] = !prevState[type],
                    _a);
            });
        };
        _this.hidePopover = function (type) {
            var _a;
            _this.setState((_a = {},
                _a[type] = false,
                _a));
        };
        _this.renderSizePicker = function () {
            if (_this.quillRef) {
                var quill = _this.quillRef.getEditor && _this.quillRef.getEditor();
                var range = (quill && quill.selection && quill.selection.getRange()) || [];
                var format = range[0] ? quill.getFormat(range[0]) : {};
                return (_jsx("select", __assign({ className: "ql-size", value: format.size || '14px' }, { children: fontSizes.map(function (fontSize) { return (_jsx("option", __assign({ value: fontSize }, { children: fontSize }), fontSize)); }) })));
            }
            return null;
        };
        _this.renderFontPicker = function () {
            if (_this.quillRef) {
                var quill = _this.quillRef.getEditor && _this.quillRef.getEditor();
                var range = (quill && quill.selection && quill.selection.getRange()) || [];
                var format = range[0] ? quill.getFormat(range[0]) : {};
                return (_jsx("select", __assign({ className: "ql-font", defaultValue: format.font || '' }, { children: FontFamilies.map(function (font) { return (_jsx("option", __assign({ value: font === 'System' ? '' : formatFontStr(font) }, { children: font }), font)); }) })));
            }
            return null;
        };
        _this.renderColorPicker = function (type) {
            if (_this.quillRef) {
                var quill_1 = _this.quillRef.getEditor && _this.quillRef.getEditor();
                var range = (quill_1 && quill_1.selection && quill_1.selection.getRange()) || [];
                var format = range[0] ? quill_1.getFormat(range[0]) : {};
                var firstColor = Array.isArray(format[type]) && format[type].length > 0
                    ? format[type][0]
                    : format[type] || '#000000';
                return (_jsx(Popover, __assign({ body: _jsx(ColorPicker, { color: firstColor, alpha: false, onChange: function (color) {
                            var value = color.hex === 'transparent' ? null : color.hex;
                            if (_this.state.currentSelection && value) {
                                var currentSelection = _this.state.currentSelection;
                                quill_1.formatText(currentSelection.index, currentSelection.length, type, value);
                            }
                            else {
                                quill_1.format(type, value); // fallback
                            }
                        }, inputStyle: { color: '#222', borderBottom: '1px solid #222' }, appStyle: _this.props.appStyle, clearSwatch: true }), bodyStyle: {
                        width: '300px',
                        height: '220px',
                        zIndex: 500,
                    }, isOpen: _this.state["".concat(type, "PopoverOpen")], preferPlace: "above", bgColor: "#eee", borderColor: "#ccc", onOuterAction: function () {
                        _this.setState({ currentSelection: '' });
                        _this.hidePopover("".concat(type, "PopoverOpen"));
                    } }, { children: _jsx("button", { className: "ql-".concat(type), type: "button", onClick: function () {
                            if (!_this.state["".concat(type, "PopoverOpen")]) {
                                if (_this.quillRef && _this.quillRef.getEditor) {
                                    _this.setState({
                                        currentSelection: _this.quillRef.getEditor().getSelection(),
                                    });
                                }
                            }
                            _this.togglePopover("".concat(type, "PopoverOpen"));
                        } }) }), type));
            }
            return null;
        };
        // Hack to make sure colors aren't toggled on and off when clicking the button
        _this.nullFunc = function () { return null; };
        _this.state = {
            displayPopover: false,
            colorPopoverOpen: false,
            backgroundPopoverOpen: false,
            imageList: [],
        };
        _this.mediaFolderPopoverRef = React.createRef();
        _this.quillID = generateShortUUID();
        _this.quillRef = React.createRef();
        return _this;
    }
    RTEditor.prototype.render = function () {
        var _this = this;
        var _a = this.props, style = _a.style, value = _a.value, onFocus = _a.onFocus, onBlur = _a.onBlur;
        return (_jsxs(_Fragment, { children: [_jsxs("div", __assign({ style: style }, { children: [this.renderToolbar(), _jsx(_Fragment, { children: _jsx(ReactQuillComponent, { id: this.quillID, value: value !== null && value !== void 0 ? value : '', ref: function (node) {
                                    _this.quillRef = node;
                                }, formats: [
                                    'header',
                                    'font',
                                    'height',
                                    'width',
                                    'style',
                                    'size',
                                    'bold',
                                    'italic',
                                    'underline',
                                    'strike',
                                    'blockquote',
                                    'list',
                                    'bullet',
                                    'link',
                                    'color',
                                    'background',
                                    'align',
                                    'image',
                                    'ImageFormat',
                                    'relativeWidth',
                                ], modules: {
                                    toolbar: {
                                        container: "#ql-editor-toolbar".concat(this.quillID),
                                        handlers: {
                                            color: this.nullFunc,
                                            background: this.nullFunc,
                                            image: this.nullFunc,
                                        },
                                        header: [1, 2, 3, 4, 5, 6, false],
                                    },
                                    clipboard: {
                                        matchVisual: false,
                                    },
                                    imageResize: {
                                        parchment: Quill.import('parchment'),
                                        modules: ['Resize', 'DisplaySize', 'Toolbar'],
                                    },
                                }, onChange: this.handleChange, theme: "snow", onFocus: onFocus, onBlur: onBlur, preserveWhitespace: true }) })] })), _jsx(Modal, __assign({ renderInto: "richText", headerTitle: "Media", open: this.state.displayPopover, onClose: this.closePopover }, { children: _jsx(MediaModal, { baseUrl: process.env.API_BASE_URL, mediaBucketURL: process.env.MEDIA_BUCKET_URL, iconBucketURL: process.env.ICON_BUCKET_URL, fileBucketURL: process.env.FILE_BUCKET_URL, aptVersion: process.env.APT_VERSION, isMediaModal: false, onSelectFile: this.onSelectFile, acceptedTypes: this.props.acceptedTypes }) }))] }));
    };
    return RTEditor;
}(React.Component));
export { RTEditor };
RTEditor.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    style: PropTypes.object,
};
export default RTEditor;
